import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, TextField, Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

const TEMPLATE = [
  { id: "hipertension",      label: "Hipertensión arterial",               sublabel: "" },
  { id: "diabetes",          label: "Diabetes mellitus",                    sublabel: "" },
  { id: "cardiopatias",      label: "Cardiopatías",                         sublabel: "IM, insuficiencia cardíaca, arritmias" },
  { id: "respiratorias",     label: "Enfermedades respiratorias",           sublabel: "Asma, EPOC, neumonía" },
  { id: "renales",           label: "Enfermedades renales",                 sublabel: "" },
  { id: "hepaticas",         label: "Enfermedades hepáticas",               sublabel: "" },
  { id: "neurologicas",      label: "Enfermedades neurológicas",            sublabel: "Epilepsia, ACV, migraña" },
  { id: "mentales",          label: "Enfermedades mentales/psicológicas",   sublabel: "" },
  { id: "cancer",            label: "Cáncer",                               sublabel: "" },
  { id: "alergias",          label: "Alergias",                             sublabel: "Medicamentos, alimentos, ambiente" },
  { id: "cirugias",          label: "Cirugías previas",                     sublabel: "" },
  { id: "hospitalizaciones", label: "Hospitalizaciones previas",            sublabel: "" },
  { id: "tabaquismo",        label: "Tabaquismo",                           sublabel: "" },
  { id: "alcoholismo",       label: "Alcoholismo",                          sublabel: "" },
  { id: "drogas",            label: "Sustancias psicoactivas",              sublabel: "" },
];

const buildRows = (saved) => {
  let parsed = saved;
  if (typeof saved === "string") {
    try { parsed = JSON.parse(saved); } catch { parsed = []; }
  }
  const list = Array.isArray(parsed) ? parsed : [];
  return TEMPLATE.map((t) => {
    const existing = list.find((r) => r.id === t.id);
    return { ...t, si: false, observaciones: "", ...existing };
  });
};

const getOtroTexto = (saved) => {
  let parsed = saved;
  if (typeof saved === "string") {
    try { parsed = JSON.parse(saved); } catch { parsed = []; }
  }
  const list = Array.isArray(parsed) ? parsed : [];
  return list.find((r) => r.id === "otro")?.descripcion || "";
};

const hasData = (rows, otroTexto) => rows.some((r) => r.si) || !!otroTexto;

const AntecedentesPersonales = forwardRef(({ patient, editing }, ref) => {
  const [editRows, setEditRows] = useState(() => buildRows(patient?.antecedentesPersonales));
  const [otroTexto, setOtroTexto] = useState(() => getOtroTexto(patient?.antecedentesPersonales));

  useEffect(() => {
    if (editing) {
      setEditRows(buildRows(patient?.antecedentesPersonales));
      setOtroTexto(getOtroTexto(patient?.antecedentesPersonales));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const rows = editRows.filter((r) => r.si || r.observaciones);
      if (otroTexto) rows.push({ id: "otro", descripcion: otroTexto });
      return rows;
    },
  }));

  const updateRow = (id, field, value) => {
    setEditRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  const viewRows = buildRows(patient?.antecedentesPersonales);
  const filled = viewRows.filter((r) => r.si || r.observaciones);
  const viewOtroTexto = getOtroTexto(patient?.antecedentesPersonales);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
        Antecedentes personales
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        ¿Ha padecido o padece alguna de las siguientes condiciones?
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {editing ? (
        <>
          <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40%" }}>Antecedente</TableCell>
                <TableCell sx={{ width: "10%" }} align="center">Sí</TableCell>
                <TableCell sx={{ width: "50%" }}>Observaciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2">{row.label}</Typography>
                    {row.sublabel && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{row.sublabel}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox size="small" checked={!!row.si} onChange={(e) => updateRow(row.id, "si", e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" fullWidth variant="standard"
                      value={row.observaciones}
                      onChange={(e) => updateRow(row.id, "observaciones", e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Otro</Typography>
            <TextField
              size="small" fullWidth multiline minRows={2} variant="outlined"
              placeholder="Otros antecedentes personales..."
              value={otroTexto}
              onChange={(e) => setOtroTexto(e.target.value)}
            />
          </Box>
        </>
      ) : !hasData(viewRows, viewOtroTexto) ? (
        <Typography variant="body2" color="text.secondary">Sin antecedentes personales registrados.</Typography>
      ) : (
        <>
          <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40%" }}>Antecedente</TableCell>
                <TableCell sx={{ width: "10%" }} align="center">Sí</TableCell>
                <TableCell sx={{ width: "50%" }}>Observaciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filled.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2">{row.label}</Typography>
                    {row.sublabel && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{row.sublabel}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.si ? <CheckIcon fontSize="small" color="primary" /> : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.observaciones || "—"}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {viewOtroTexto && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Otro</Typography>
              <Typography variant="body2">{viewOtroTexto}</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

export default AntecedentesPersonales;
